import { useCursor } from "@/contexts/cursor"
import { useStore, useViewport } from "@xyflow/react"
import { memo, useMemo } from "react"

export const SelectionPreview = memo(() => {
	const { remoteCursors, peers } = useCursor()
	const viewport = useViewport()
	const nodes = useStore((state) => state.nodes)

	const selectingCursors = useMemo(() => {
		return Array.from(remoteCursors.values()).filter(
			(c) => c.state === "selecting" && c.selectionBox,
		)
	}, [remoteCursors])

	if (selectingCursors.length === 0) return null

	return (
		<div className="pointer-events-none absolute inset-0 overflow-hidden">
			{selectingCursors.map((cursor) => {
				const { selectionBox } = cursor
				if (!selectionBox) return null

				const userInfo = peers.get(cursor.connectionId)

				// Selection box is sent in flow/canvas coordinates
				// Transform to screen coordinates using the receiver's viewport
				const screenX = selectionBox.x * viewport.zoom + viewport.x
				const screenY = selectionBox.y * viewport.zoom + viewport.y
				const screenWidth = selectionBox.width * viewport.zoom
				const screenHeight = selectionBox.height * viewport.zoom

				return (
					<div key={`selection-group-${cursor.connectionId}`}>
						{/* Selection box */}
						<div
							key={`selection-${cursor.connectionId}`}
							className="pointer-events-none absolute"
							style={{
								transform: `translate(${screenX}px, ${screenY}px)`,
								width: screenWidth,
								height: screenHeight,
								border:
									selectionBox.mode === "partial"
										? `2px dashed ${userInfo?.color ?? "#888"}cc`
										: `1px solid ${userInfo?.color ?? "#888"}cc`,
								backgroundColor: `${userInfo?.color ?? "#888"}15`,
							}}
						>
							<div
								className="absolute -top-6 left-0 whitespace-nowrap rounded px-1.5 py-0.5 text-xs text-white shadow-sm"
								style={{ backgroundColor: userInfo?.color ?? "#888" }}
							>
								{userInfo?.name ?? "User"} selecting
								{selectionBox.mode === "partial" ? " (partial)" : ""}
							</div>
						</div>

						{/* Node highlights */}
						{selectionBox.selectedNodeIds?.map((nodeId) => {
							const node = nodes.find((n) => n.id === nodeId)
							if (!node || !node.measured) return null

							// Get node dimensions from measured or default size
							const width = node.measured?.width ?? node.width ?? 280
							const height = node.measured?.height ?? node.height ?? 100

							const nodeScreenX = node.position.x * viewport.zoom + viewport.x
							const nodeScreenY = node.position.y * viewport.zoom + viewport.y

							return (
								<div
									key={`selection-node-${cursor.connectionId}-${nodeId}`}
									className="pointer-events-none absolute rounded-lg"
									style={{
										transform: `translate(${nodeScreenX}px, ${nodeScreenY}px)`,
										width: width * viewport.zoom,
										height: height * viewport.zoom,
										border: `2px solid ${userInfo?.color ?? "#888"}`,
										boxShadow: `0 0 0 2px ${userInfo?.color ?? "#888"}33`,
									}}
								/>
							)
						})}
					</div>
				)
			})}
		</div>
	)
})
